<template>
  <div v-if="estructuraEfectiva.length > 0">
    <AdjuntosEmpresaAgentesCardList
      ref="documentCardList"
      :estructura-documental="estructuraEfectiva"
      :modo-edicion="modoEdicion"
      :with-external-upload-btn="syncDocumentacionExterna"
      @documents:upload="onDocumentsUpload"
      @state:changed="onAdjuntosStateChanged"
    />
  </div>
</template>

<script setup lang="ts">
import AdjuntosEmpresaAgentesCardList from "./AdjuntosEmpresaAgentesCardList.vue";
import type { EstructuraDocumentalEmpresaAgentesModel, TipoDocumentoEmpresaAgentesEnum } from "@api/api";
import { computed, ref } from "vue";
import { toastError, toastSuccess } from "@services/notification-service";
import { useApi } from "@api/integration";
import { useIntl } from "@services/i18n/i18nservice";
import type { CardFile } from "@components/document-card/models/CardFile";

const props = withDefaults(defineProps<{
  idEmpresaAgentes: string | null;
  estructuraDocumental: EstructuraDocumentalEmpresaAgentesModel[];
  syncDocumentacionExterna?: boolean;
  /** Si es true, no se exige ningún tipo aunque el API marque Requerido (formularios alta/edición empresa) */
  documentosOpcionales?: boolean;
  modoEdicion?: "all" | "only-new-files" | "none";
}>(), {
  modoEdicion: "all",
});

const syncDocumentacionExterna = computed(() => !!props.syncDocumentacionExterna);

const estructuraEfectiva = computed((): EstructuraDocumentalEmpresaAgentesModel[] => {
  const rows = props.estructuraDocumental ?? [];
  if (!props.documentosOpcionales) {
    return rows;
  }
  return rows.map((row) => ({ ...row, Requerido: false }));
});

const emit = defineEmits(["documentoSubido", "documentacion-changed"]);

const documentCardList = ref<InstanceType<typeof AdjuntosEmpresaAgentesCardList>>();
const api = useApi();
const intl = useIntl();

const onDocumentsUpload = async () => {
  if (syncDocumentacionExterna.value) {
    return;
  }
  try {
    await sincronizarCambiosDocumentacion();
    emit("documentoSubido");
  } catch {
    toastError(intl.i18n.t("empresa_agentes.documentacion.guardar_fallo"));
  }
};

const onAdjuntosStateChanged = (): void => {
  emit("documentacion-changed");
};

async function validarDocumentos(): Promise<boolean> {
  const isValid = documentCardList.value?.isValid() ?? false;

  if (!isValid) {
    toastError(intl.i18n.t("empresa_agentes.documentacion.documento_validaciones_fallo"));
    return false;
  }
  return true;
}

export type SincronizarDocumentacionEmpresaAgentesOpciones = {
  suprimirToastExito?: boolean;
};

const sincronizarCambiosDocumentacion = async (
  idEmpresaOverride?: string | null,
  opciones?: SincronizarDocumentacionEmpresaAgentesOpciones,
): Promise<void> => {
  const idEmpresa = idEmpresaOverride ?? props.idEmpresaAgentes;
  if (!idEmpresa) {
    return;
  }

  const isValid = await validarDocumentos();
  if (!isValid) {
    return;
  }

  const files = documentCardList.value?.getFiles();

  if (files === undefined) {
    return;
  }

  const filesToDelete = files.filter((f) => f.status === "deleted" || f.status === "modified");
  const filesToUpload = files.filter((f) => f.status === "new" || f.status === "modified" || f.status === "copied");

  await eliminarDocumentacion(filesToDelete, idEmpresa);
  await guardarDocumentacion(filesToUpload, idEmpresa);

  if (!opciones?.suprimirToastExito) {
    toastSuccess(intl.i18n.t("empresa_agentes.documentacion.guardar_exito"));
  }
};

const guardarDocumentacion = async (files: CardFile[], idEmpresa: string) => {
  const savePromises = files.map((file) => {
    const typeCode = file.typeCode as TipoDocumentoEmpresaAgentesEnum;
    return api.callStandardApi(
      "postDocumentoEmpresaAgentes",
      { showLoading: true },
      idEmpresa,
      typeCode,
      file.fileName,
      file.file!,
    );
  });

  await Promise.all(savePromises);
};

const eliminarDocumentacion = async (files: CardFile[], idEmpresa: string) => {
  const deletePromises = files.map((file) => {
    return api.callStandardApi("deleteDocumentoEmpresaAgentes", { showLoading: true }, idEmpresa, file.id);
  });
  await Promise.all(deletePromises);
};

const hayDocumentacionPendienteDeSubir = (files: CardFile[]): boolean => {
  return files.some(
    (f) => f.status === "new" || f.status === "modified" || f.status === "deleted" || f.status === "copied",
  );
};

const tipoRequeridoTieneAdjuntoServidorOPendiente = (tipo: TipoDocumentoEmpresaAgentesEnum): boolean => {
  const rows = estructuraEfectiva.value;
  const tieneEnServidor = rows.some(
    (r) =>
      r.TipoDocumento === tipo &&
      r.IdDocumentoAdjunto != null &&
      String(r.IdDocumentoAdjunto).trim() !== "",
  );
  if (tieneEnServidor) {
    return true;
  }
  const files = documentCardList.value?.getFiles() ?? [];
  return files.some(
    (f) =>
      String(f.typeCode) === String(tipo) &&
      (f.status === "new" || f.status === "modified" || f.status === "copied") &&
      (f.file != null || (f.remoteUri != null && f.remoteUri !== "") || (f.uri != null && f.uri !== "")),
  );
};

const isDocumentacionValid = (): boolean => {
  const lista = documentCardList.value;
  const rows = estructuraEfectiva.value;
  if (rows.length === 0) {
    return true;
  }
  if (!lista) {
    return false;
  }
  if (!lista.isValid()) {
    return false;
  }
  const tiposRequeridos = [
    ...new Set(rows.filter((r) => r.Requerido).map((r) => r.TipoDocumento)),
  ];
  const requeridosOk = tiposRequeridos.every((tipo) =>
    tipoRequeridoTieneAdjuntoServidorOPendiente(tipo),
  );
  if (!requeridosOk) {
    return false;
  }
  if (syncDocumentacionExterna.value) {
    return true;
  }
  const files = lista.getFiles();
  return !hayDocumentacionPendienteDeSubir(files);
};

const hayCambiosPendientesDocumentacion = (): boolean => {
  const lista = documentCardList.value;
  if (!lista || estructuraEfectiva.value.length === 0) {
    return false;
  }
  const files = lista.getFiles();
  return hayDocumentacionPendienteDeSubir(files);
};

defineExpose({
  isDocumentacionValid,
  sincronizarCambiosDocumentacion,
  hayCambiosPendientesDocumentacion,
});
</script>
